// What every lens other than actions shares: actions grouped into runs, each run the consecutive actions the same
// performers (a role, a system) do before the work passes to someone or something else, and the hand-overs between
// runs. A performer appears once for each separate run, so the graph reads as the process collapsed by who, or what,
// does the work. Built from the analysis graph, so it can be tested without a page
import { AnalysisEdge, AnalysisNode } from '../common/analysis-model';
import { NODE_SUPERTYPE } from '../common/linqset-model';
import type { LensGraphEdge, LensGraphNode } from './lens-node-style';

// the steps of a run, first to last, for its card
export const stepsFact = (actionNames: string[]) => actionNames.length <= 6 ? actionNames.join(' → ') : `${actionNames.slice(0, 6).join(' → ')} … and ${actionNames.length - 6} more`;

export interface LensSegment {
    modelKey: string;
    share: number;                      // of the run's work
}

// what a run of a lens has, whatever the lens
export interface LensRun {
    key: string;
    name: string;
    actionKeys: string[];               // its actions, first to last, so a simulation can be rolled up to it
    actionNames: string[];
    hours: number;                      // work a year
    segments: LensSegment[];            // how that work splits between models, largest first
}

export interface Handover {
    key: string;
    from: string;                       // run keys
    to: string;
    items: number;                      // items a year handed over
    actionPairs: [string, string][];    // the connections between actions that make it up, for the simulation
}

// an output of the models shown: the information the work produces, where it ends
export interface LensOutput {
    key: string;                        // 'output:' and the information's key, so it can't clash with a run's
    infoKey: string;
    name: string;
    modelKey: string;
    items: number;                      // a year
}

export interface RunGroup {
    key: string;
    performerKey: string;
    actions: AnalysisNode[];            // first to last
    hours: number;
    segments: LensSegment[];
}

// groups the actions of the models shown into runs, joining actions where the same performers do both ends of a
// connection, and finds the hand-overs between runs. Runs come largest (by hours) first, hand-overs busiest first
export const buildRuns = (analysis: { nodes: AnalysisNode[]; edges: AnalysisEdge[] }, performerKey: (a: AnalysisNode) => string, modelKeys?: Set<string>) => {
    const shown = (modelKey: string) => !modelKeys || modelKeys.has(modelKey);
    const actions = new Map(analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.ACTION && shown(n.modelKey)).map(n => [n._key, n]));
    const connections = analysis.edges.filter(e => actions.has(e.from) && actions.has(e.to) && e.from !== e.to);

    const parent = new Map<string, string>([...actions.keys()].map(k => [k, k]));
    const find = (k: string): string => {
        let root = k;
        while (parent.get(root) !== root) root = parent.get(root)!;
        let next = k;
        while (parent.get(next) !== root) { const up = parent.get(next)!; parent.set(next, root); next = up; }
        return root;
    };
    connections.forEach(e => {
        if (performerKey(actions.get(e.from)!) === performerKey(actions.get(e.to)!)) parent.set(find(e.from), find(e.to));
    });
    const groups = new Map<string, string[]>();
    actions.forEach((_, k) => (groups.get(find(k)) || groups.set(find(k), []).get(find(k))!).push(k));

    // a run's actions in the order work moves through them: those it starts with first
    const order = (keys: string[]) => {
        const inside = new Set(keys);
        const incoming = new Map(keys.map(k => [k, 0]));
        connections.forEach(e => { if (inside.has(e.from) && inside.has(e.to)) incoming.set(e.to, (incoming.get(e.to) || 0) + 1); });
        const ready = keys.filter(k => !incoming.get(k));
        const sorted: string[] = [];
        while (ready.length) {
            const k = ready.shift()!;
            sorted.push(k);
            connections.forEach(e => {
                if (e.from !== k || !inside.has(e.to)) return;
                const left = (incoming.get(e.to) || 0) - 1;
                incoming.set(e.to, left);
                if (left === 0) ready.push(e.to);
            });
        }
        // anything in a loop goes at the end, in the order given
        return [...sorted, ...keys.filter(k => !sorted.includes(k))];
    };

    const runOf = new Map<string, string>();
    const runs: RunGroup[] = [...groups.values()].map(keys => {
        const ordered = order(keys).map(k => actions.get(k)!);
        const key = `run:${ordered[0]._key}`;
        ordered.forEach(a => runOf.set(a._key, key));
        const byModel = new Map<string, number>();
        let hours = 0;
        ordered.forEach(a => {
            hours += a.hours || 0;
            byModel.set(a.modelKey, (byModel.get(a.modelKey) || 0) + (a.hours || 0));
        });
        const segments = [...byModel.entries()].map(([modelKey, h]) => ({ modelKey, share: hours ? h / hours : 1 / byModel.size })).sort((a, b) => b.share - a.share);
        return { key, performerKey: performerKey(ordered[0]), actions: ordered, hours, segments };
    }).sort((a, b) => b.hours - a.hours);

    const handovers = new Map<string, Handover>();
    connections.forEach(e => {
        const from = runOf.get(e.from)!, to = runOf.get(e.to)!;
        if (from === to) return;
        const key = `${from}>${to}`;
        const h = handovers.get(key) || handovers.set(key, { key, from, to, items: 0, actionPairs: [] }).get(key)!;
        h.items += e.items || 0;
        h.actionPairs.push([e.from, e.to]);
    });

    // outputs, and the runs that create them: a link from a run to an output is a hand-over the simulation can show
    const outputs = new Map<string, LensOutput>();
    const outputLinks = new Map<string, Handover>();
    const infos = new Map(analysis.nodes.filter(n => n.superType === NODE_SUPERTYPE.INFORMATION && shown(n.modelKey)).map(n => [n._key, n]));
    analysis.edges.forEach(e => {
        const info = infos.get(e.to), from = runOf.get(e.from);
        if (!info || !from) return;
        const key = `output:${info._key}`;
        if (!outputs.has(key)) outputs.set(key, { key, infoKey: info._key, name: info.name || 'Unnamed output', modelKey: info.modelKey, items: info.items || 0 });
        const linkKey = `${from}>${key}`;
        const link = outputLinks.get(linkKey) || outputLinks.set(linkKey, { key: linkKey, from, to: key, items: 0, actionPairs: [] }).get(linkKey)!;
        link.items += e.items || 0;
        link.actionPairs.push([e.from, info._key]);
    });

    return {
        runs, handovers: [...handovers.values()].sort((a, b) => b.items - a.items), actions, connections,
        outputs: [...outputs.values()].sort((a, b) => b.items - a.items), outputLinks: [...outputLinks.values()],
    };
};

// the graph's nodes and edges for a lens's outputs, the same in every lens
export const outputsGraph = (outputs: LensOutput[], links: Handover[], runNames: Map<string, string>, modelColours: Map<string, string>,
    count: (v: number) => string, modelNames?: Map<string, string>): { nodes: LensGraphNode[]; edges: LensGraphEdge[] } => {
    const names = new Map(outputs.map(o => [o.key, o.name]));
    return {
        nodes: outputs.map(o => ({
            key: o.key, label: o.name, kind: 'output' as const, detail: `Output · ${count(o.items)} a year`, dashed: false, status: 'none',
            segments: [{ colour: modelColours.get(o.modelKey) || '#a9a8a3', share: 1 }],
            tooltip: `${o.name}: an output, ${count(o.items)} items a year`,
            tip: { kind: 'Output', title: o.name, where: modelNames?.get(o.modelKey), facts: [{ label: 'Place', value: 'Where the work ends: what the models produce' }],
                figures: [{ label: 'Items', value: count(o.items), note: 'a year' }] },
        })),
        edges: links.map(l => ({
            key: l.key, from: l.from, to: l.to, weight: l.items, colour: '#94a3b8',
            tooltip: `${runNames.get(l.from) || 'Unnamed'} creates ${names.get(l.to)}: ${count(l.items)} items a year`,
        })),
    };
};

// the graph's tooltips and details count actions this way
export const actionCount = (n: number) => `${n} ${n === 1 ? 'action' : 'actions'}`;
